import {Component, forwardRef, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator} from '@angular/forms';
import {CustomRoleDto, CustomRoleService} from '../../../_services/configuration-services';
import {Subscription} from 'rxjs';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {TranslateUtils} from '../../translate-utils';

@Component({
    selector: 'app-custom-role-management',
    templateUrl: './custom-role-management.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomRoleManagementComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CustomRoleManagementComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomRoleManagementComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {

    @Input() enablePublicAccess = false;
    @Input() enableAllCustomers = false;
    @Input() validationRequired = false;

    customerBaseRole: CustomRoleDto | null = null;
    roles: CustomRoleDto[] = [];
    roleCheckboxes: boolean[] = [];
    isPublicAccess = false;
    isAllCustomers = false;
    loaded = false;
    isDisabled = false;
    crtLang = TranslateUtils.defaultLanguage;

    private pendingValue: CustomRoleDto[] | null | undefined = undefined;
    private onChange: (value: CustomRoleDto[] | null) => void = () => {};
    private onTouched: () => void = () => {};
    private onValidatorChange: () => void = () => {};
    private langChangeSubscription: Subscription;

    constructor(
        private readonly customRoleService: CustomRoleService,
        private readonly translateService: TranslateService
    ) {
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.crtLang = event.lang;
        });

        this.customRoleService.getAllCustomRoles(false).subscribe(allRoles => {
            this.customerBaseRole = allRoles.find(r => r.isBase && r.roleKey === 'Customer') ?? null;
            this.roles = allRoles.filter(r => !r.isBase).sort((a, b) => a.roleKey.localeCompare(b.roleKey));
            this.roleCheckboxes = this.roles.map(() => false);

            if (this.pendingValue !== undefined) {
                this.applyValue(this.pendingValue);
                this.pendingValue = undefined;
            }

            this.loaded = true;
            this.onValidatorChange();
        });
    }

    ngOnDestroy(): void {
        this.langChangeSubscription?.unsubscribe();
    }

    // ControlValueAccessor

    writeValue(value: CustomRoleDto[] | null): void {
        if (!this.loaded) {
            this.pendingValue = value;
            return;
        }
        this.applyValue(value);
    }

    registerOnChange(fn: (value: CustomRoleDto[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    // Validator

    validate(control: AbstractControl): ValidationErrors | null {
        if (!this.validationRequired || !this.loaded) {
            return null;
        }
        const value = control.value as CustomRoleDto[] | null;
        if (this.isPublicAccess || (value !== null && value.length > 0)) {
            return null;
        }
        return { noneSelected: true };
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    // Event handlers

    onPublicToggle(): void {
        if (this.isPublicAccess) {
            this.isAllCustomers = false;
            this.roleCheckboxes = this.roles.map(() => false);
        }
        this.emitValue();
        this.onTouched();
    }

    onAllCustomersToggle(): void {
        if (this.isAllCustomers) {
            this.roleCheckboxes = this.roles.map(() => false);
        }
        this.emitValue();
        this.onTouched();
    }

    onRoleChange(): void {
        this.emitValue();
        this.onTouched();
    }

    // Private helpers

    private applyValue(value: CustomRoleDto[] | null): void {
        // null is treated as [] for backward compatibility with data saved before this semantic change
        const roles = value ?? [];

        if (roles.some(r => r.roleKey === 'Customer')) {
            // The Customer base role in the array means "all customers" — activate the corresponding toggle
            this.isPublicAccess = false;
            this.isAllCustomers = true;
            this.roleCheckboxes = this.roles.map(() => false);
        } else if (roles.length === 0) {
            // An empty array means public access when the option is available, otherwise nothing is selected.
            this.isPublicAccess = this.enablePublicAccess;
            this.isAllCustomers = false;
            this.roleCheckboxes = this.roles.map(() => false);
        } else {
            // Specific custom roles: match each loaded role against the incoming value by roleKey
            this.isPublicAccess = false;
            this.isAllCustomers = false;
            this.roleCheckboxes = this.roles.map(role =>
                roles.some(selected => selected.roleKey === role.roleKey)
            );
        }
    }

    private emitValue(): void {
        if (this.isPublicAccess) {
            // Emit [] for public access — consistent with the legacy role-management component
            this.onChange([]);
        } else if (this.isAllCustomers) {
            // Emit [customerBaseRoleDto] so the backend can persist the Customer base role relationship.
            this.onChange(this.customerBaseRole ? [this.customerBaseRole] : []);
        } else {
            this.onChange(this.roles.filter((_, i) => this.roleCheckboxes[i]));
        }
        this.onValidatorChange();
    }
}
